import React, { useState } from "react";
import { X, Loader2, FileText, Home } from "lucide-react";
import PropertyInfo from "./PropertyInfo";

const NewCaseModal = ({
  isNewCaseOpen,
  setIsNewCaseOpen,
  handleCaseSubmit,
  caseForm,
  setCaseForm,
  handleLoanAmountChange,
  isSubmitting,
  newCaseError,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [propertyData, setPropertyData] = useState({});

  if (!isNewCaseOpen) return null;

  const handlePropertyDataChange = (data) => {
    setPropertyData(data);
    // Optionally sync with caseForm
    if (data.homePrice) {
      setCaseForm(prev => ({
        ...prev,
        loanAmount: `$${(data.homePrice - data.downPayment).toLocaleString()}`
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Merge property data with case form before submitting
    const enrichedForm = {
      ...caseForm,
      propertyInfo: propertyData
    };
    handleCaseSubmit(e, enrichedForm);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 dark:bg-slate-900/80 bg-slate-500/50 backdrop-blur-sm"
      onClick={() => setIsNewCaseOpen(false)}
    >
      <div
        className="rounded-2xl max-w-5xl w-full shadow-2xl border animate-scale-in max-h-[90vh] overflow-hidden flex flex-col dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-800 border-slate-200">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              New Case
            </p>
            <h3 className="text-xl font-bold dark:text-white text-slate-900">
              Create Underwriting
            </h3>
          </div>
          <button
            onClick={() => setIsNewCaseOpen(false)}
            className="p-2 rounded-full transition-colors text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b dark:border-slate-800 border-slate-200 px-6">
          <button
            onClick={() => setActiveTab("details")}
            className={`pb-4 pt-2 px-4 font-semibold text-sm transition-colors relative ${activeTab === "details"
              ? "dark:text-yellow-400 text-yellow-600"
              : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Case Details
            </div>
            {activeTab === "details" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 dark:bg-yellow-400 bg-yellow-600"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab("property")}
            className={`pb-4 pt-2 px-4 font-semibold text-sm transition-colors relative ${activeTab === "property"
              ? "dark:text-yellow-400 text-yellow-600"
              : "text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200"
              }`}
          >
            <div className="flex items-center gap-2">
              <Home className="w-4 h-4" />
              Property Info
            </div>
            {activeTab === "property" && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 dark:bg-yellow-400 bg-yellow-600"></div>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6">
            {newCaseError && (
              <div className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
                {newCaseError}
              </div>
            )}

            {/* Case Details Tab */}
            {activeTab === "details" && (
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="case-borrower-name"
                    className="block text-sm font-semibold text-slate-500 mb-1"
                  >
                    Borrower Name
                  </label>
                  <input
                    id="case-borrower-name"
                    type="text"
                    value={caseForm.name}
                    onChange={(event) =>
                      setCaseForm({ ...caseForm, name: event.target.value })
                    }
                    placeholder="Jane Doe"
                    required
                    className="w-full rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="case-borrower-email"
                      className="block text-sm font-semibold text-slate-500 mb-1"
                    >
                      Borrower Email
                    </label>
                    <input
                      id="case-borrower-email"
                      type="email"
                      value={caseForm.email}
                      onChange={(event) =>
                        setCaseForm({ ...caseForm, email: event.target.value })
                      }
                      placeholder="jane.doe@example.com"
                      required
                      className="w-full rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="case-borrower-phone"
                      className="block text-sm font-semibold text-slate-500 mb-1"
                    >
                      Borrower Phone
                    </label>
                    <input
                      id="case-borrower-phone"
                      type="tel"
                      value={caseForm.phone}
                      onChange={(event) =>
                        setCaseForm({ ...caseForm, phone: event.target.value })
                      }
                      placeholder="(555) 123-4567"
                      className="w-full rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="case-property-address"
                    className="block text-sm font-semibold text-slate-500 mb-1"
                  >
                    Property Address
                  </label>
                  <input
                    id="case-property-address"
                    type="text"
                    value={caseForm.propertyAddress}
                    onChange={(event) =>
                      setCaseForm({ ...caseForm, propertyAddress: event.target.value })
                    }
                    placeholder="123 Main St, City, State"
                    required
                    className="w-full rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="case-loan-type"
                      className="block text-sm font-semibold text-slate-500 mb-1"
                    >
                      Loan Type
                    </label>
                    <select
                      id="case-loan-type"
                      value={caseForm.loanType}
                      onChange={(event) =>
                        setCaseForm({ ...caseForm, loanType: event.target.value })
                      }
                      className="w-full rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900"
                    >
                      {["Conventional", "FHA", "VA", "Jumbo", "DSCR"].map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="case-loan-amount"
                      className="block text-sm font-semibold text-slate-500 mb-1"
                    >
                      Loan Amount
                    </label>
                    <input
                      id="case-loan-amount"
                      type="text"
                      value={caseForm.loanAmount}
                      onChange={handleLoanAmountChange}
                      placeholder="$450,000"
                      required
                      className="w-full rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900"
                    />
                  </div>
                  <div>
                    <label htmlFor="case-target-close-date" className="block text-sm font-semibold text-slate-500 mb-1">
                      Target Close Date
                    </label>
                    <input
                      id="case-target-close-date"
                      type="date"
                      value={caseForm.targetCloseDate}
                      onChange={(event) => setCaseForm({ ...caseForm, targetCloseDate: event.target.value })}
                      required
                      className="w-full rounded-lg px-3 py-2 focus:border-yellow-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-slate-100 text-slate-900"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Property Info Tab */}
            {activeTab === "property" && (
              <PropertyInfo
                initialData={propertyData}
                onChange={handlePropertyDataChange}
              />
            )}

            {/* Footer Buttons */}
            <div className="flex justify-end gap-3 pt-6 mt-6 border-t dark:border-slate-800 border-slate-200">
              <button
                type="button"
                onClick={() => setIsNewCaseOpen(false)}
                className="px-4 py-2 rounded-lg border transition-colors dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-700 dark:hover:bg-slate-800 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg bg-yellow-600 text-white font-semibold hover:bg-yellow-500 disabled:opacity-60 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSubmitting ? "Creating..." : "Create Case"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewCaseModal;
